import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Clock,
  Calendar,
  FolderKanban,
  Users,
  CheckSquare,
  TrendingUp,
  ArrowRight,
  Briefcase,
  Palmtree,
  Calculator,
  Play,
  Pause,
  Square,
  Coffee,
  LogIn,
  LogOut,
  AlertTriangle,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { VacationReminderBanner } from '../components/VacationReminderBanner';
import CategoryRevenueWidget from '../components/CategoryRevenueWidget';
import { formatHoursToHM } from '../lib/formatTime';
import { formatTimeBerlin } from '../lib/dateTime';
import { MyWeeksOverview } from '../components/MyWeeksOverview';

// Extracted to prevent re-render issues
const DashboardStatCard = ({ icon: Icon, label, value, subtext, color, delay, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
    onClick={onClick}
    className={`card-glass p-6 ${onClick ? 'cursor-pointer hover:bg-accent transition-colors group' : ''}`}
  >
    <div className="flex items-start justify-between mb-4">
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          color === 'indigo'
            ? 'bg-indigo-500/20 text-indigo-400'
            : color === 'lime'
            ? 'bg-lime-500/20 text-lime-400'
            : color === 'amber'
            ? 'bg-amber-500/20 text-amber-400'
            : color === 'emerald'
            ? 'bg-emerald-500/20 text-emerald-400'
            : 'bg-red-500/20 text-red-400'
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
    </div>
    {onClick && <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />}
    <p className="text-3xl font-heading font-bold text-foreground mb-1">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
    {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
  </motion.div>
);

export default function DashboardPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [clockStatus, setClockStatus] = useState(null);
  const [clockError, setClockError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(null);
  const [missingWeeks, setMissingWeeks] = useState(null);

  const fetchClockStatus = useCallback(async () => {
    try {
      const res = await api.get('/clock/status');
      setClockStatus(res.data);
      setClockError(false);
    } catch (error) {
      console.error('Failed to fetch clock status:', error);
      setClockError(true);
    }
  }, [api]);

  useEffect(() => {
    // Start all fetches concurrently
    const fetchStats = api.get('/dashboard/stats').then(res => {
      setStats(res.data);
      setIsLoading(false);
    }).catch(err => {
      console.error('Failed to fetch dashboard stats:', err);
      setIsLoading(false);
    });

    const fetchClock = fetchClockStatus();

    const fetchMissing = api.get('/approvals/my-missing?weeks=4').then(res => {
      setMissingWeeks(res.data?.missing || []);
    }).catch(err => {
      console.error('Failed to fetch missing weeks:', err);
      setMissingWeeks([]);
    });

    const fetchPending = (user?.role === 'supervisor' || user?.role === 'admin')
      ? api.get('/approvals/pending/count').then(res => {
          setPendingApprovalsCount(res.data?.count || 0);
        }).catch(err => {
          console.error('Failed to fetch pending approvals:', err);
          setPendingApprovalsCount(0);
        })
      : Promise.resolve();

    Promise.allSettled([fetchStats, fetchClock, fetchMissing, fetchPending]);
  }, [api, user?.role, fetchClockStatus]);

  // Einmaliger automatischer Neuversuch nach Fehler
  useEffect(() => {
    if (!clockError || clockStatus !== null) return;
    const timer = setTimeout(() => fetchClockStatus(), 10000);
    return () => clearTimeout(timer);
  }, [clockError, clockStatus, fetchClockStatus]);

  // Update clock status every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (clockStatus?.status === 'clocked_in' || clockStatus?.status === 'on_break') {
        fetchClockStatus();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [clockStatus?.status, fetchClockStatus]);

  const handleClockIn = async () => {
    setClockLoading(true);
    try {
      await api.post('/clock/in');
      toast.success('Eingestempelt');
      await fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einstempeln');
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    setClockLoading(true);
    try {
      const res = await api.post('/clock/out');
      // Use session_hours from response (not worked_hours)
      toast.success(`Ausgestempelt - ${formatHoursToHM(res.data.session_hours)} gearbeitet`);
      await fetchClockStatus();
      // Refresh stats to update today's work hours
      const statsRes = await api.get('/dashboard/stats');
      setStats(statsRes.data);
    } catch (error) {
      // Handle 409: auto-closed session
      if (error.response?.status === 409) {
        toast.warning(error.response.data.detail, { duration: 10000 });
        await fetchClockStatus();
        // Refresh stats as well
        const statsRes = await api.get('/dashboard/stats');
        setStats(statsRes.data);
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Ausstempeln');
      }
    } finally {
      setClockLoading(false);
    }
  };

  const handleStartBreak = async () => {
    setClockLoading(true);
    try {
      await api.post('/clock/break/start');
      toast.success('Pause gestartet');
      await fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setClockLoading(false);
    }
  };

  const handleEndBreak = async () => {
    setClockLoading(true);
    try {
      const res = await api.post('/clock/break/end');
      toast.success(`Pause beendet - ${res.data.break_minutes} Minuten`);
      await fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setClockLoading(false);
    }
  };

  const formatMinutesToTime = (minutes) => {
    if (!minutes || minutes < 0) return '0h 0min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  // § 4 ArbZG: Ruhepause zählt erst ab 15 Minuten; 30 Min ab 6 h, 45 Min ab 9 h Arbeitszeit
  const getBreakStatusToday = () => {
    const breaks = clockStatus?.breaks_today || [];
    const workedMinutes = clockStatus?.worked_minutes || 0;
    const qualifying = breaks
      .filter((b) => (b.duration_minutes || 0) >= 15)
      .reduce((sum, b) => sum + (b.duration_minutes || 0), 0);
    const required = workedMinutes > 540 ? 45 : workedMinutes > 360 ? 30 : 0;
    return { qualifying, required, missing: Math.max(0, required - qualifying) };
  };
  
  const getStatusInfo = () => {
    if (!clockStatus || clockStatus.status === 'clocked_out') {
      return {
        icon: LogIn,
        text: 'Nicht eingestempelt',
        color: 'text-muted-foreground',
        bgColor: 'bg-muted',
        dotColor: 'bg-muted-foreground'
      };
    }
    if (clockStatus.status === 'on_break') {
      return {
        icon: Coffee,
        text: `In Pause seit ${formatTimeBerlin(clockStatus.break_start)}`,
        color: 'text-amber-600 dark:text-amber-400',
        bgColor: 'bg-amber-500/20',
        dotColor: 'bg-amber-500 animate-pulse'
      };
    }
    return {
      icon: Clock,
      text: `Eingestempelt seit ${formatTimeBerlin(clockStatus.clock_in)}`,
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-500/20',
      dotColor: 'bg-emerald-500 animate-pulse'
    };
  };

  const statusInfo = getStatusInfo();
  const breakStatusToday = getBreakStatusToday();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Vacation Reminder Banner */}
      <VacationReminderBanner />

      {/* Missing Weeks Banner */}
      {missingWeeks === null ? (
        <div className="h-14 rounded-lg bg-muted/30 animate-pulse" />
      ) : missingWeeks.length > 0 && (
        <div 
          className="flex items-center justify-between p-4 rounded-lg bg-amber-500/10 border border-amber-500/30"
          data-testid="missing-weeks-banner"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <span className="text-foreground">
              <span className="font-medium">{missingWeeks.length} Woche{missingWeeks.length !== 1 ? 'n' : ''}</span> noch nicht eingereicht:{' '}
              {missingWeeks.map((w, i) => (
                <span key={w.week_start}>
                  {i > 0 && ', '}KW {w.iso_week}
                </span>
              ))}
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => navigate(`/time-tracking?week=${missingWeeks[0].week_start}`)}
            className="bg-amber-500 hover:bg-amber-600 text-white flex-shrink-0"
            data-testid="missing-weeks-submit-btn"
          >
            Jetzt einreichen
          </Button>
        </div>
      )}

      {/* Review Entries Banner - fehlende Endzeiten */}
      {clockStatus?.review_entries?.length > 0 && (
        <div 
          className="flex items-center justify-between p-4 rounded-lg bg-amber-500/10 border border-amber-500/30"
          data-testid="review-entries-banner"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-800 dark:text-amber-300 flex-shrink-0" />
            <span className="text-amber-800 dark:text-amber-300">
              {clockStatus.review_entries.length === 1 ? (
                <>
                  Am {new Date(clockStatus.review_entries[0].date + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}. wurde nicht ausgestempelt (Beginn {clockStatus.review_entries[0].start_time}). Bitte Endzeit nachtragen.
                </>
              ) : (
                <>
                  An <span className="font-medium">{clockStatus.review_entries.length} Tagen</span> fehlt die Endzeit:{' '}
                  {clockStatus.review_entries.map((entry, i) => (
                    <span key={entry.id}>
                      {i > 0 && ', '}
                      <Link 
                        to={`/time-tracking?week=${entry.date}&entry=${entry.id}`}
                        className="underline hover:no-underline"
                      >
                        {new Date(entry.date + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}.
                      </Link>
                    </span>
                  ))}
                </>
              )}
            </span>
          </div>
          {clockStatus.review_entries.length === 1 && (
            <Button
              type="button"
              size="sm"
              onClick={() => navigate(`/time-tracking?week=${clockStatus.review_entries[0].date}&entry=${clockStatus.review_entries[0].id}`)}
              className="bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 flex-shrink-0"
              data-testid="review-entry-submit-btn"
            >
              Nachtragen
            </Button>
          )}
        </div>
      )}

      {/* Header */}
      <div>
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl md:text-3xl font-heading font-bold text-foreground"
        >
          Guten {new Date().getHours() < 12 ? 'Morgen' : new Date().getHours() < 18 ? 'Tag' : 'Abend'}, {user?.first_name}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-muted-foreground mt-1"
        >
          {format(new Date(), "EEEE, d. MMMM yyyy", { locale: de })}
        </motion.p>
      </div>

      {/* Time Clock Widget */}
      {clockStatus === null && !clockError ? (
        <div className="card-glass p-6 h-24 animate-pulse">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-muted/50" />
            <div className="space-y-2">
              <div className="h-5 w-40 rounded bg-muted/50" />
              <div className="h-4 w-24 rounded bg-muted/50" />
            </div>
          </div>
        </div>
      ) : clockStatus === null && clockError ? (
        <div className="card-glass p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="time-clock-error">
          <p className="text-sm text-muted-foreground">Stempelstatus konnte nicht geladen werden.</p>
          <Button
            type="button"
            variant="outline"
            onClick={fetchClockStatus}
            data-testid="clock-status-retry-button"
            className="h-10"
          >
            Erneut versuchen
          </Button>
        </div>
      ) : (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="card-glass p-6"
        data-testid="time-clock-widget"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          {/* Status Display */}
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${statusInfo.bgColor}`}>
              <statusInfo.icon className={`w-7 h-7 ${statusInfo.color}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${statusInfo.dotColor}`} />
                <p className={`text-lg font-medium ${statusInfo.color}`}>
                  {statusInfo.text}
                </p>
              </div>
              {clockStatus?.status !== 'clocked_out' && (
                <p className="text-2xl font-heading font-bold text-foreground mt-1">
                  {formatMinutesToTime(clockStatus?.worked_minutes || 0)}
                  <span className="text-sm text-muted-foreground ml-2 font-normal">Arbeitszeit heute</span>
                </p>
              )}
              {/* Show "seit gestern" hint for night shift */}
              {clockStatus?.session_started_yesterday && clockStatus?.clock_in && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                  Eingestempelt seit gestern, {formatTimeBerlin(clockStatus.clock_in)} Uhr
                </p>
              )}
              {(clockStatus?.breaks_today?.length > 0 || clockStatus?.status === 'on_break') && (
                <p className="text-sm text-muted-foreground mt-1">
                  <span>Pausen heute: </span>
                  {(clockStatus.breaks_today || []).map((b, i) => (
                    <span key={i}>
                      {i > 0 ? ' · ' : ''}
                      {b.start_time}–{b.end_time} ({b.duration_minutes} Min.)
                    </span>
                  ))}
                  {clockStatus?.status === 'on_break' && (
                    <span className="text-amber-600 dark:text-amber-400">
                      {(clockStatus.breaks_today || []).length > 0 ? ' · ' : ''}
                      seit {formatTimeBerlin(clockStatus.break_start)} laufend
                    </span>
                  )}
                  {(clockStatus.breaks_today || []).length > 1 && (
                    <span> – gesamt {clockStatus.total_break_minutes} Minuten</span>
                  )}
                </p>
              )}

              {breakStatusToday.missing > 0 && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-1 flex items-start gap-1">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Noch {breakStatusToday.missing} Minuten Ruhepause nötig (§ 4 ArbZG). Pausen unter 15 Minuten zählen
                    dafür nicht.
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {clockStatus?.status === 'clocked_out' && (
              <Button
                onClick={handleClockIn}
                disabled={clockLoading}
                data-testid="clock-in-button"
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 h-12 text-base"
              >
                <Play className="w-5 h-5 mr-2" />
                Einstempeln
              </Button>
            )}

            {clockStatus?.status === 'clocked_in' && (
              <>
                <Button
                  onClick={handleStartBreak}
                  disabled={clockLoading}
                  data-testid="start-break-button"
                  variant="outline"
                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 px-5 h-12"
                >
                  <Coffee className="w-5 h-5 mr-2" />
                  Pause
                </Button>
                <Button
                  onClick={handleClockOut}
                  disabled={clockLoading}
                  data-testid="clock-out-button"
                  className="bg-red-500 hover:bg-red-600 text-white px-6 h-12"
                >
                  <Square className="w-5 h-5 mr-2" />
                  Ausstempeln
                </Button>
              </>
            )}

            {clockStatus?.status === 'on_break' && (
              <>
                <Button
                  onClick={handleEndBreak}
                  disabled={clockLoading}
                  data-testid="end-break-button"
                  className="bg-amber-500 hover:bg-amber-600 text-white px-6 h-12 text-base"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Pause beenden
                </Button>
                <Button
                  onClick={handleClockOut}
                  disabled={clockLoading}
                  data-testid="clock-out-button"
                  className="bg-red-500 hover:bg-red-600 text-white px-6 h-12"
                >
                  <Square className="w-5 h-5 mr-2" />
                  Ausstempeln
                </Button>
              </>
            )}
          </div>
        </div>
      </motion.div>
      )}

      {/* Main Stats - Different for Employee vs Admin/Supervisor */}
      {user?.role === 'employee' ? (
        // Employee: Essential info with work/project time summary
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Arbeitszeitkonto Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card-glass p-6"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  (stats?.time_account_balance || 0) >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'
                }`}>
                  <Calculator className={`w-6 h-6 ${
                    (stats?.time_account_balance || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                  }`} />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">Arbeitszeitkonto</h3>
                  <p className="text-sm text-muted-foreground">
                    {(stats?.time_account_balance || 0) >= 0 ? 'Guthaben' : 'Minusstunden'}
                  </p>
                </div>
              </div>
              <p className={`text-4xl font-heading font-bold ${
                (stats?.time_account_balance || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {formatHoursToHM(stats?.time_account_balance || 0, true)}
              </p>
            </motion.div>

            {/* Resturlaub Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="card-glass p-6"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-500/20">
                  <Palmtree className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">Resturlaub</h3>
                  
                </div>
              </div>
              <p className="text-5xl font-heading font-bold text-amber-600 dark:text-amber-400">
                {stats?.vacation_days_remaining || 0} <span className="text-xl text-muted-foreground">Tage</span>
              </p>
            </motion.div>
          </div>

          {/* Arbeitszeit & Projektzeit Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-glass p-6"
          >
            <h3 className="text-lg font-medium text-foreground mb-4">Zeitübersicht</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Arbeitszeit heute</p>
                <p className="text-xl font-mono font-bold text-indigo-600 dark:text-indigo-400">{formatHoursToHM(stats?.today_work_hours || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Projektzeit heute</p>
                <p className="text-xl font-mono font-bold text-lime-600 dark:text-lime-400">{formatHoursToHM(stats?.today_project_hours || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Arbeitszeit Woche</p>
                <p className="text-xl font-mono font-bold text-indigo-600 dark:text-indigo-400">{formatHoursToHM(stats?.week_work_hours || 0)}</p>
                <p className="text-xs text-muted-foreground">von {user?.weekly_hours || 40}h</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Projektzeit Woche</p>
                <p className="text-xl font-mono font-bold text-lime-600 dark:text-lime-400">{formatHoursToHM(stats?.week_project_hours || 0)}</p>
              </div>
            </div>
          </motion.div>

          {/* Meine Wochen - Status-Übersicht */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card-glass p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-foreground">Meine Wochen</h3>
                <p className="text-sm text-muted-foreground">Genehmigungsstatus der letzten Wochen</p>
              </div>
            </div>
            <MyWeeksOverview weeksCount={12} />
          </motion.div>
        </div>
      ) : (
        // Admin/Supervisor: Full stats view
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <DashboardStatCard
              icon={Briefcase}
              label="Heute erfasst"
              value={formatHoursToHM(stats?.today_work_hours || 0)}
              color="indigo"
              delay={0.2}
            />
            <DashboardStatCard
              icon={FolderKanban}
              label="Auftragszeit heute"
              value={formatHoursToHM(stats?.today_project_hours || 0)}
              color="lime"
              delay={0.25}
            />
            <DashboardStatCard
              icon={Calculator}
              label="Arbeitszeitkonto"
              value={formatHoursToHM(stats?.time_account_balance || 0, true)}
              subtext={stats?.time_account_balance >= 0 ? 'Zeitguthaben' : 'Minusstunden'}
              color={stats?.time_account_balance >= 0 ? 'emerald' : 'red'}
              delay={0.3}
            />
            <DashboardStatCard
              icon={Palmtree}
              label="Resturlaub"
              value={`${stats?.vacation_days_remaining?.toFixed(1) || 0} Tage`}
              subtext={`${stats?.vacation_days_used || 0} genommen${stats?.vacation_days_planned > 0 ? `, ${stats?.vacation_days_planned} geplant` : ''}${stats?.shutdown_days > 0 ? `, ${stats?.shutdown_days} BF` : ''}`}
              color="amber"
              delay={0.35}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {/* Offene Genehmigungen - Klickbar */}
            {pendingApprovalsCount === null ? (
              <div className="card-glass p-6 animate-pulse">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-muted/50" />
                </div>
                <div className="h-8 w-12 rounded bg-muted/50 mb-1" />
                <div className="h-4 w-32 rounded bg-muted/50" />
              </div>
            ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              onClick={() => navigate('/approvals')}
              className="card-glass p-6 cursor-pointer hover:bg-accent transition-colors group"
              data-testid="pending-approvals-card"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  pendingApprovalsCount > 0 ? 'bg-amber-500/20' : 'bg-emerald-500/20'
                }`}>
                  <CheckSquare className={`w-5 h-5 ${
                    pendingApprovalsCount > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
                  }`} />
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
              <p className={`text-3xl font-heading font-bold mb-1 ${
                pendingApprovalsCount > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {pendingApprovalsCount}
              </p>
              <p className="text-sm text-muted-foreground">Offene Genehmigungen</p>
              {pendingApprovalsCount > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400/70 mt-1">Klicken zum Bearbeiten</p>
              )}
            </motion.div>
            )}
            
            <DashboardStatCard
              icon={Palmtree}
              label="Abwesenheitsanträge"
              value={stats?.pending_vacation_requests || 0}
              color="emerald"
              delay={0.45}
              onClick={() => navigate('/approvals?tab=leaves')}
            />
            <DashboardStatCard
              icon={Users}
              label="Mitarbeiter"
              value={stats?.users_count || 0}
              color="indigo"
              delay={0.5}
            />
            <DashboardStatCard
              icon={FolderKanban}
              label="Aktive Aufträge"
              value={stats?.projects_count || 0}
              color="lime"
              delay={0.55}
            />
          </div>
        </>
      )}

      {/* Category Revenue Widget - Only for Admin/Accounting/Supervisor */}
      <CategoryRevenueWidget />

      {/* Quick Actions - Simplified for Employee */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card-glass p-6"
      >
        <h2 className="text-lg font-heading font-semibold text-foreground mb-4">Schnellaktionen</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/time-tracking"
            className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-accent transition-colors"
          >
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <span className="text-foreground">Zeiterfassung</span>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
          </Link>
          <Link
            to="/leave"
            className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-accent transition-colors"
          >
            <div className="flex items-center gap-3">
              <Palmtree className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <span className="text-foreground">Abwesenheit beantragen</span>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
          </Link>
          <Link
            to="/time-account"
            className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-accent transition-colors"
          >
            <div className="flex items-center gap-3">
              <Calculator className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-foreground">Arbeitszeitkonto</span>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
